import { useState } from "react";
import {
  Box, Typography, Paper, Stack, TextField, Button, Alert, Chip, FormControlLabel, Switch, MenuItem, Divider, Grid2 as Grid,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxConsentType } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const CONSENTS: DxConsentType[] = ["Radiation", "Contrast", "MriSafety"];

export function DiagnosticsSafetyPage() {
  const canAssess = useAuth(s => s.hasPermission("diagnostics.safety.assess"));
  const canApprove = useAuth(s => s.hasPermission("diagnostics.safety.approve"));
  const qc = useQueryClient();
  const [studyId, setStudyId] = useState("");
  const [active, setActive] = useState("");

  const safety = useQuery({ queryKey: ["dx-safety", active], queryFn: () => diagnosticsApi.getSafety(active), enabled: !!active });
  const a = safety.data ?? null;
  const refresh = () => qc.invalidateQueries({ queryKey: ["dx-safety", active] });

  // questionnaire state
  const [pregnant, setPregnant] = useState(false); const [suspected, setSuspected] = useState(false); const [lmp, setLmp] = useState(""); const [breastfeeding, setBreastfeeding] = useState(false);
  const [allergy, setAllergy] = useState(false); const [asthma, setAsthma] = useState(false); const [renal, setRenal] = useState(false);
  const [reactions, setReactions] = useState(""); const [meds, setMeds] = useState(""); const [creatinine, setCreatinine] = useState("");

  const assess = useMutation({
    meta: { successMessage: "Safety screening saved" },
    mutationFn: () => diagnosticsApi.assessSafety({
      studyId: active || studyId,
      isPregnant: pregnant, suspectedPregnancy: suspected, lastMenstrualPeriod: lmp || null, breastfeeding,
      contrastAllergy: allergy, asthma, renalDisease: renal, previousReactions: reactions || null, currentMedications: meds || null,
      creatinine: creatinine === "" ? null : Number(creatinine),
    }),
    onSuccess: () => { setActive(active || studyId); refresh(); },
  });

  // consent + approval + clear
  const [consentType, setConsentType] = useState<DxConsentType>("Radiation"); const [consentBy, setConsentBy] = useState(""); const [consentSig, setConsentSig] = useState("");
  const [approveSig, setApproveSig] = useState("");
  const addConsent = useMutation({ meta: { successMessage: "Consent recorded" }, mutationFn: () => diagnosticsApi.addSafetyConsent(a!.id, { type: consentType, signedBy: consentBy || null, signature: consentSig }), onSuccess: () => { setConsentSig(""); refresh(); } });
  const approve = useMutation({ meta: { successMessage: "Pregnancy approved by radiologist" }, mutationFn: () => diagnosticsApi.approvePregnancy(a!.id, { signature: approveSig }), onSuccess: () => { setApproveSig(""); refresh(); } });
  const clear = useMutation({ meta: { successMessage: "Safety cleared — procedure may start" }, mutationFn: () => diagnosticsApi.clearSafety(a!.id), onSuccess: refresh });

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>Diagnostics · Radiology Safety</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1}>
          <TextField label="Study Id" size="small" value={studyId} onChange={e => setStudyId(e.target.value)} sx={{ flexGrow: 1 }} />
          <Button variant="outlined" onClick={() => setActive(studyId)} disabled={!studyId}>Load</Button>
        </Stack>
        {a && (
          <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={a.status} color={a.status === "Cleared" ? "success" : a.status === "Blocked" ? "error" : "default"} />
            {a.pregnancyApplicable && <Chip size="small" variant="outlined" label="Ionizing — pregnancy screen" />}
            {a.contrastApplicable && <Chip size="small" variant="outlined" label="Contrast" />}
            {a.creatinineAlert && <Chip size="small" color="warning" label="High creatinine" />}
            {a.radiologistApprovalRequired && <Chip size="small" color={a.pregnancyApprovedAt ? "success" : "error"} label={a.pregnancyApprovedAt ? "Pregnancy approved" : "Pregnancy approval required"} />}
          </Stack>
        )}
      </Paper>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Pregnancy screening</Typography>
            <Stack spacing={0.5}>
              <FormControlLabel control={<Switch checked={pregnant} onChange={e => setPregnant(e.target.checked)} />} label="Pregnant" />
              <FormControlLabel control={<Switch checked={suspected} onChange={e => setSuspected(e.target.checked)} />} label="Suspected pregnancy" />
              <TextField label="Last menstrual period" type="date" size="small" InputLabelProps={{ shrink: true }} value={lmp} onChange={e => setLmp(e.target.value)} />
              <FormControlLabel control={<Switch checked={breastfeeding} onChange={e => setBreastfeeding(e.target.checked)} />} label="Breastfeeding" />
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" gutterBottom>Contrast safety</Typography>
            <Stack spacing={0.5}>
              <FormControlLabel control={<Switch checked={allergy} onChange={e => setAllergy(e.target.checked)} />} label="Contrast allergy" />
              <FormControlLabel control={<Switch checked={asthma} onChange={e => setAsthma(e.target.checked)} />} label="Asthma" />
              <FormControlLabel control={<Switch checked={renal} onChange={e => setRenal(e.target.checked)} />} label="Renal disease" />
              <TextField label="Previous reactions" size="small" value={reactions} onChange={e => setReactions(e.target.value)} />
              <TextField label="Current medications" size="small" value={meds} onChange={e => setMeds(e.target.value)} />
              <TextField label="Serum creatinine (mg/dL)" type="number" size="small" value={creatinine} onChange={e => setCreatinine(e.target.value)} helperText="Alert above 1.5" />
            </Stack>
            {assess.isError && <Alert severity="error" sx={{ mt: 1 }}>{errorMessage(assess.error)}</Alert>}
            <Button variant="contained" sx={{ mt: 1.5 }} disabled={!canAssess || (!studyId && !active) || assess.isPending} onClick={() => assess.mutate()}>Save screening</Button>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Consents & clearance</Typography>
            {!a && <Typography variant="body2" color="text.secondary">Save the screening first.</Typography>}
            {a && (
              <Stack spacing={1.5}>
                <Typography variant="caption" color="text.secondary">Required: {a.requiredConsents.join(", ") || "none"}</Typography>
                <Stack spacing={0.5}>
                  {a.consents.map((c, i) => <Chip key={i} size="small" color="success" variant="outlined" label={`${c.type} · ${c.signedBy ?? ""}`} />)}
                </Stack>
                {canAssess && a.status !== "Cleared" && (
                  <Stack direction="row" spacing={1}>
                    <TextField select label="Consent" size="small" value={consentType} onChange={e => setConsentType(e.target.value as DxConsentType)} sx={{ width: 130 }}>
                      {CONSENTS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                    <TextField label="Signed by" size="small" value={consentBy} onChange={e => setConsentBy(e.target.value)} sx={{ width: 120 }} />
                    <TextField label="Signature" size="small" value={consentSig} onChange={e => setConsentSig(e.target.value)} sx={{ flexGrow: 1 }} />
                    <Button variant="outlined" disabled={!consentSig || addConsent.isPending} onClick={() => addConsent.mutate()}>Add</Button>
                  </Stack>
                )}

                {a.radiologistApprovalRequired && !a.pregnancyApprovedAt && canApprove && (
                  <>
                    <Divider />
                    <Alert severity="warning">Pregnancy detected/suspected — radiologist approval required.</Alert>
                    <Stack direction="row" spacing={1}>
                      <TextField label="Radiologist signature" size="small" value={approveSig} onChange={e => setApproveSig(e.target.value)} sx={{ flexGrow: 1 }} />
                      <Button variant="contained" color="warning" disabled={!approveSig || approve.isPending} onClick={() => approve.mutate()}>Approve</Button>
                    </Stack>
                  </>
                )}

                {(addConsent.isError || approve.isError || clear.isError) && <Alert severity="error">{errorMessage(addConsent.error ?? approve.error ?? clear.error)}</Alert>}
                {a.status !== "Cleared" && canAssess && (
                  <Button variant="contained" color="success" disabled={clear.isPending} onClick={() => clear.mutate()}>Clear for procedure</Button>
                )}
                {a.status === "Cleared" && <Alert severity="success">Cleared — the procedure may start.</Alert>}
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
